import React from "react";
import { 
  Shield, 
  Eye, 
  UserCheck, 
  Lock, 
  Scale, 
  Database,
  ArrowRight,
  ClipboardList,
  Server,
  Mail,
  Activity,
  ShieldAlert
} from "lucide-react";
import Navigation from "../components/Navigation";
import FooterSection from "../components/FooterSection";
import "./PrivacyPolicy.css";

const PrivacyPolicy = () => {
  return (
    <div className="pp-page-shell">
      <Navigation />
      
      {/* Sovereign Visual Overlays */}
      <div className="pp-tactical-grid"></div>
      <div className="pp-scanline-overlay"></div>
      <div className="pp-laser-scanner"></div>
      <div className="pp-radial-depth"></div>

      <main className="pp-content-hub">
        
        {/* Header Node */}
        <header className="pp-hero-sector">
          <div className="pp-badge-container">
            <div className="pp-hud-badge">
              <Shield size={14} className="pp-icon-blue pp-pulse" />
              <span>COMPLIANCE_PROTOCOL_V4.2</span>
            </div>
          </div>
          <h1 className="pp-hero-title">DATA <span className="pp-highlight">SOVEREIGNTY</span></h1>
          <div className="pp-hero-subtitle">
            <span>LAST_UPDATED: JAN_2026</span>
            <span className="pp-sep">|</span>
            <span>STATUS: ACTIVE_ENCRYPTION</span>
          </div>
        </header>

        {/* Tactical Summary Grid */}
        <section className="pp-summary-grid">
          <div className="pp-summary-card pp-glass">
            <div className="pp-summary-icon-box">
              <Shield size={24} />
            </div>
            <h3>HIPAA_CERTIFIED</h3>
            <p>All clinical data assets are encrypted and stored in compliance with top-tier international medical standards.</p>
            <div className="pp-card-brackets"></div>
          </div>
          
          <div className="pp-summary-card pp-glass">
            <div className="pp-summary-icon-box green">
              <Eye size={24} />
            </div>
            <h3>TRANSPARENCY_HUD</h3>
            <p>Maintain 100% visibility over your health data telemetry. Monitor access logs directly via the command center.</p>
            <div className="pp-card-brackets"></div>
          </div>

          <div className="pp-summary-card pp-glass">
            <div className="pp-summary-icon-box orange">
              <UserCheck size={24} />
            </div>
            <h3>IDENTITY_RIGHTS</h3>
            <p>Sovereign control over your data. Export, archive, or purge medical assets with zero third-party interference.</p>
            <div className="pp-card-brackets"></div>
          </div>
        </section>

        {/* Detailed Protocol Stack */}
        <div className="pp-protocol-stack">
          
          <section className="pp-protocol-section pp-glass">
            <div className="pp-section-header">
              <ClipboardList size={28} className="pp-icon-blue" />
              <h2>01. DATA_ACQUISITION</h2>
            </div>
            <div className="pp-section-body">
              <div className="pp-data-node">
                <h4>TELEMETRY_BIOMETRICS</h4>
                <p>We continuously synchronize vital sign data from connected IoT nodes, including cardiac rhythm, oxygen saturation (SpO2), core body temperature, and ECG waveform diagnostics.</p>
              </div>
              <div className="pp-data-node">
                <h4>IDENTITY_METADATA</h4>
                <p>Registration requires verified identifiers: legal name, encrypted communication channels, and clinical history. This data forms the core of your Sovereign Medical Identity.</p>
              </div>
            </div>
          </section>

          <section className="pp-protocol-section pp-glass">
            <div className="pp-section-header">
              <Server size={28} className="pp-icon-green" />
              <h2>02. DATA_UTILIZATION</h2>
            </div>
            <div className="pp-section-body">
              <ul className="pp-usage-list">
                <li>
                  <Activity size={18} className="pp-icon-blue" />
                  <div>
                    <strong>REALTIME_MONITORING:</strong> Processing raw telemetry into tactical dashboard readouts for authorized medical oversight.
                  </div>
                </li>
                <li>
                  <ShieldAlert size={18} className="pp-icon-orange" />
                  <div>
                    <strong>AUTONOMOUS_ALERTS:</strong> Triggering priority protocols when biometrics deviate from established clinical baselines.
                  </div>
                </li>
              </ul>
            </div>
          </section>

          <section className="pp-protocol-section pp-glass">
            <div className="pp-section-header">
              <Lock size={28} className="pp-icon-orange" />
              <h2>03. SECURITY_HARDENING</h2>
            </div>
            <div className="pp-section-body">
              <p className="pp-margin-btm">Hardened security layers protect all sensitive health metadata within the Medico ecosystem:</p>
              <div className="pp-security-matrix">
                <div className="pp-matrix-item">
                  <h4>AES_256_ENCRYPTION</h4>
                  <p>Military-grade encryption for all medical assets at rest and in transit.</p>
                </div>
                <div className="pp-matrix-item">
                  <h4>MFA_GATEKEEPING</h4>
                  <p>Multi-factor authorization required for all high-level system interactions.</p>
                </div>
              </div>
            </div>
          </section>

          <section className="pp-protocol-section pp-glass">
            <div className="pp-section-header">
              <Scale size={28} className="pp-icon-cyan" />
              <h2>04. SOVEREIGN_RIGHTS</h2>
            </div>
            <div className="pp-section-body">
              <div className="pp-rights-matrix">
                <div className="pp-right-node">
                  <ArrowRight size={20} className="pp-icon-blue" />
                  <div>
                    <strong>ACCESS_PROTOCOL:</strong> Request full extraction of all medical data assets associated with your identity.
                  </div>
                </div>
                <div className="pp-right-node">
                  <Database size={20} className="pp-icon-blue" />
                  <div>
                    <strong>PURGE_SEQUENCE:</strong> Initiate permanent erasure of stored health assets (subject to clinical legal retention).
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="pp-protocol-section pp-glass">
            <div className="pp-section-header">
              <Mail size={28} className="pp-icon-blue" />
              <h2>SYSTEM_COMMUNICATIONS</h2>
            </div>
            <div className="pp-section-body">
              <div className="pp-contact-node pp-glass-inner">
                <p><strong>ENCRYPTED_MAIL:</strong> compliance@medico.in</p>
                <p><strong>SECURE_LINE:</strong> +1 (800) MED-IDEN</p>
              </div>
              <button className="pp-protocol-btn">
                INITIALIZE_LEGAL_SYNC
              </button>
            </div>
          </section>

        </div>
      </main>

      <FooterSection />

    </div>
  );
};

export default PrivacyPolicy;
